import React, { useMemo } from 'react';
import { Card, CardContent, Typography, Divider, Box, Paper } from '@mui/material';
import MathArit2Operand1PhaseStudent from './AssignmentTypes/MathArit2Operand1PhaseStudent';
import MathAritRoundingStudent from './AssignmentTypes/MathAritRoundingStudent';
import FillInTheBlanksStudent from './AssignmentTypes/FillInTheBlanksStudent';
import PairingStudent from './AssignmentTypes/PairingStudent';
import MaterialBasedStudent from './AssignmentTypes/MaterialBasedStudent';
import MaterialBasedMultichoiceStudent from './AssignmentTypes/MaterialBasedMultichoiceStudent';
import SortToBoxesStudent from './AssignmentTypes/SortToBoxesStudent';
import OrderingStudent from './AssignmentTypes/OrderingStudent';
import LearningMaterialContent from '../LearningMaterial/LearningMaterialContent';

interface LinkedMaterial {
  id: number;
  title: string;
  content: string;
  showToStudent?: boolean;
}

interface StudentAssignmentProps {
  type: string;
  config: any;
  linkedMaterial?: LinkedMaterial | null;
  instructions: string;
  order: number;
  additionalInfo?: string;
  onAnswerChange: (index: number, answer: any) => void;
  onSubmitAnswers: () => void;
  disabled: boolean;
  feedback: any;
  initialAnswers: any;
  assignmentStatus: 'New' | 'Active' | 'InReview';
  hasSubmitted: boolean;
}

const StudentAssignment: React.FC<StudentAssignmentProps> = ({
  type,
  config,
  linkedMaterial,
  instructions,
  order,
  additionalInfo,
  onAnswerChange,
  disabled,
  feedback,
  initialAnswers,
  assignmentStatus,
  hasSubmitted,
}) => {
  // Moved useMemo hooks to the top level
  const initialPairingAnswers = useMemo(() => {
    if (type === 'pairing') {
      return config.questions.reduce(
        (
          acc: { [key: string]: { id: string; text: string } },
          question: { id: string; termBOptions: string[] },
          index: number
        ) => {
          const answerText = initialAnswers[index] || '';
          const optionIndex = question.termBOptions.findIndex((opt) => opt === answerText);
          if (optionIndex !== -1) {
            const optionId = `${question.id}-option-${optionIndex}`;
            acc[question.id] = { id: optionId, text: answerText };
          } else if (answerText) {
            acc[question.id] = { id: '', text: answerText };
          }
          return acc;
        },
        {}
      );
    }
    return {};
  }, [type, config.questions, initialAnswers]);

  const initialAssignedTerms = useMemo(() => {
    if (type === 'sort_to_boxes') {
      const terms = config.terms || [];
      if (Array.isArray(initialAnswers) && initialAnswers.length === terms.length) {
        return terms.map((term: any, index: number) => {
          const assignedContainerId = initialAnswers[index];
          return {
            ...term,
            containerId: assignedContainerId || '',
          };
        });
      } else {
        // If no initial answers or mismatch in length, use the terms from config
        return terms;
      }
    }
    return [];
  }, [type, config.terms, initialAnswers]);

  const usesBuiltInMaterialView = [
    'material_based_ai_assisted',
    'material_based_multichoice',
    'reading_comprehension_multichoice',
    'true_false',
  ].includes(type);
  const shouldShowSharedMaterial = Boolean(linkedMaterial?.showToStudent) && !usesBuiltInMaterialView;

  const renderExercise = () => {
    switch (type) {
      case 'math_arit_2operand_1phase':
        return (
          <MathArit2Operand1PhaseStudent
            config={config}
            onAnswerChange={onAnswerChange}
            disabled={disabled}
            feedback={feedback}
            initialAnswers={initialAnswers}
            assignmentStatus={assignmentStatus}
            hasSubmitted={hasSubmitted}
          />
        );
      case 'math_arit_rounding_precision':
        return (
          <MathAritRoundingStudent
            config={config}
            onAnswerChange={onAnswerChange}
            disabled={disabled}
            feedback={feedback}
            initialAnswers={initialAnswers}
            assignmentStatus={assignmentStatus}
            hasSubmitted={hasSubmitted}
          />
        );
      case 'math_arit_2operand_2phase':
        return (
          <MathArit2Operand1PhaseStudent
            config={config}
            onAnswerChange={onAnswerChange}
            disabled={disabled}
            feedback={feedback}
            initialAnswers={initialAnswers}
            assignmentStatus={assignmentStatus}
            hasSubmitted={hasSubmitted}
          />
        );
      case 'fill_in_the_blanks': {
        const initialAnswersObj = config.questions.reduce(
          (acc: { [key: string]: string }, question: { id: string }, index: number) => {
            acc[question.id] = initialAnswers[index] || '';
            return acc;
          },
          {}
        );

        const handleAnswerChangeForBlanks = (questionId: string, answer: string) => {
          const questionIndex = config.questions.findIndex((q: { id: string }) => q.id === questionId);
          if (questionIndex !== -1) {
            onAnswerChange(questionIndex, answer);
          }
        };

        return (
          <FillInTheBlanksStudent
            config={config}
            onAnswerChange={handleAnswerChangeForBlanks}
            disabled={disabled}
            initialAnswers={initialAnswersObj}
            assignmentStatus={assignmentStatus}
            hasSubmitted={hasSubmitted}
          />
        );
      }
      case 'pairing': {
        const handleAnswerChangeForPairing = (
          questionId: string,
          answer: { id: string; text: string }
        ) => {
          const questionIndex = config.questions.findIndex((q: { id: string }) => q.id === questionId);
          if (questionIndex !== -1) {
            onAnswerChange(questionIndex, answer.text);
          }
        };

        return (
          <PairingStudent
            config={config}
            onAnswerChange={handleAnswerChangeForPairing}
            disabled={disabled}
            initialAnswers={initialPairingAnswers}
            assignmentStatus={assignmentStatus}
            hasSubmitted={hasSubmitted}
          />
        );
      }
      case 'material_based_ai_assisted': {
        const initialMaterialAnswers: { [key: string]: string } = {};
        config.questions.forEach((question: string, index: number) => {
          initialMaterialAnswers[index.toString()] = initialAnswers[index] || '';
        });

        const handleAnswerChangeForMaterialBased = (questionId: string, answer: string) => {
          const questionIndex = parseInt(questionId, 10);
          if (!isNaN(questionIndex)) {
            onAnswerChange(questionIndex, answer);
          }
        };

        const transformedFeedback =
          feedback?.map((item: any) => ({
            grade: item.grade,
            feedback: item.feedback,
            score: item.score,
          })) || [];

        return (
          <MaterialBasedStudent
            config={config}
            linkedMaterial={linkedMaterial}
            onAnswerChange={handleAnswerChangeForMaterialBased}
            disabled={disabled}
            initialAnswers={initialMaterialAnswers}
            assignmentStatus={assignmentStatus}
            hasSubmitted={hasSubmitted}
            feedback={transformedFeedback}
          />
        );
      }
      case 'material_based_multichoice':
      case 'reading_comprehension_multichoice':
      case 'true_false': {
        const initialMultichoiceAnswers = config.questions.map((question: any, index: number) => ({
          questionId: question.id,
          selectedOptionIds: initialAnswers[index] ? 
            (Array.isArray(initialAnswers[index]) ? initialAnswers[index] : [initialAnswers[index]]) 
            : []
        }));

        const handleAnswerChangeForMultichoice = (questionId: string, selectedOptionIds: string[]) => {
          const questionIndex = config.questions.findIndex((q: any) => q.id === questionId);
          if (questionIndex !== -1) {
            onAnswerChange(questionIndex, selectedOptionIds);
          }
        };

        const transformedFeedback = feedback?.map((item: any) => ({
          isCorrect: item.isCorrect,
          correctAnswer: item.correctAnswer,
          userAnswer: item.userAnswer,
        })) || [];

        return (
          <MaterialBasedMultichoiceStudent
            config={config}
            linkedMaterial={linkedMaterial}
            initialAnswers={initialMultichoiceAnswers}
            onAnswerChange={handleAnswerChangeForMultichoice}
            disabled={disabled}
            assignmentStatus={assignmentStatus}
            hasSubmitted={hasSubmitted}
            feedback={transformedFeedback}
          />
        );
      }
      case 'sort_to_boxes': {
        const handleAnswerChangeForSortToBoxes = (updatedTerms: any[]) => {
          const answers = updatedTerms.map((term) => term.containerId || '');
          answers.forEach((answer, index) => {
            onAnswerChange(index, answer);
          });
        };

        return (
          <SortToBoxesStudent
            config={config}
            onAnswerChange={handleAnswerChangeForSortToBoxes}
            disabled={disabled}
            feedback={feedback}
            initialAssignedTerms={initialAssignedTerms}
            assignmentStatus={assignmentStatus}
            hasSubmitted={hasSubmitted}
          />
        );
      }
      case 'ordering': {
        const handleAnswerChangeForOrdering = (index: number, itemId: string) => {
          onAnswerChange(index, itemId);
        };

        return (
          <OrderingStudent
            config={config}
            onAnswerChange={handleAnswerChangeForOrdering}
            disabled={disabled}
            feedback={feedback}
            initialAnswers={initialAnswers}
            assignmentStatus={assignmentStatus}
            hasSubmitted={hasSubmitted}
          />
        );
      }
      default:
        return (
          <Typography variant="body1" color="error">
            Unknown exercise type: {type}
          </Typography>
        );
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Tehtävä {order}: {instructions}
        </Typography>
        {additionalInfo && (
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Lisätietoja: {additionalInfo}
          </Typography>
        )}

        {shouldShowSharedMaterial && linkedMaterial && (
          <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {linkedMaterial.title}
            </Typography>
            <LearningMaterialContent
              material={linkedMaterial as any}
              defaultView="sectioned"
              allowViewToggle={true}
              allowHide={true}
            />
          </Paper>
        )}

        <Divider sx={{ marginY: 2 }} />

        <Box mt={2}>{renderExercise()}</Box>
      </CardContent>
    </Card>
  );
};

export default StudentAssignment;
